import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { fetchAllUsers, approveUser, rejectUser } from '../api';

const {
  FiX,
  FiRefreshCw,
  FiShield,
  FiMail,
  FiClock,
  FiUserCheck,
  FiUserX,
  FiCheck,
} = FiIcons;

export default function AdminPanel({ isOpen, onClose, themeColors: tc }) {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('pending');

  const loadUsers = async () => {
    setLoading(true);
    try {
      const users = await fetchAllUsers();
      setAllUsers(users);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadUsers();
  }, [isOpen]);

  const handleApprove = async (u) => {
    await approveUser(u.id);
    await loadUsers();
  };

  const handleReject = async (u) => {
    await rejectUser(u.id);
    await loadUsers();
  };

  const tabs = [
    { key: 'pending', label: 'Pending', icon: FiClock, color: '#f59e0b' },
    { key: 'approved', label: 'Approved', icon: FiUserCheck, color: '#22c55e' },
    { key: 'rejected', label: 'Rejected', icon: FiUserX, color: '#ef4444' },
  ];

  const displayed = allUsers.filter((u) => u.role === tab);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-black"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 24 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed inset-0 z-[90] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col"
              style={{
                backgroundColor: tc.card,
                border: `1px solid ${tc.border}`,
                maxHeight: '80vh',
              }}
            >
              {/* Header */}
              <div
                className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
                style={{ borderColor: tc.border }}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${tc.accent}, ${tc.accent}88)`,
                    }}
                  >
                    <SafeIcon icon={FiShield} className="w-4 h-4 text-black" />
                  </div>

                  <div>
                    <p className="text-sm font-semibold" style={{ color: tc.text }}>
                      Admin Panel
                    </p>
                    <p className="text-[10px]" style={{ color: tc.muted }}>
                      Manage user access
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={loadUsers}
                    disabled={loading}
                    className="p-1.5 rounded-lg transition-colors"
                    style={{ color: tc.muted }}
                  >
                    <SafeIcon
                      icon={FiRefreshCw}
                      className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}
                    />
                  </button>

                  <button
                    onClick={onClose}
                    className="p-1.5 rounded-lg transition-colors"
                    style={{ color: tc.muted }}
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div
                className="flex border-b flex-shrink-0"
                style={{ borderColor: tc.border }}
              >
                {tabs.map(({ key, label, icon, color }) => {
                  const count = allUsers.filter((u) => u.role === key).length;

                  return (
                    <button
                      key={key}
                      onClick={() => setTab(key)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-all border-b-2"
                      style={{
                        borderColor: tab === key ? color : 'transparent',
                        color: tab === key ? color : tc.muted,
                      }}
                    >
                      <SafeIcon icon={icon} className="w-3 h-3" />
                      {label}

                      {count > 0 && (
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{ backgroundColor: `${color}20`, color }}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* User list */}
              <div
                className="flex-1 overflow-y-auto p-4 space-y-2"
                style={{ scrollbarWidth: 'none' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div
                      className="w-6 h-6 rounded-full border-2 animate-spin"
                      style={{
                        borderColor: `${tc.accent}44`,
                        borderTopColor: tc.accent,
                      }}
                    />
                  </div>
                ) : displayed.length === 0 ? (
                  <div className="text-center py-12 text-sm" style={{ color: tc.muted }}>
                    No {tab} users
                  </div>
                ) : (
                  displayed.map((u) => (
                    <motion.div
                      key={u.id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-between px-4 py-3 rounded-xl"
                      style={{
                        backgroundColor: tc.card,
                        border: `1px solid ${tc.border}`,
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${tc.accent}20` }}
                        >
                          <SafeIcon
                            icon={FiMail}
                            className="w-3.5 h-3.5"
                            style={{ color: tc.accent }}
                          />
                        </div>

                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate" style={{ color: tc.text }}>
                            {u.email}
                          </p>
                          <p className="text-[10px]" style={{ color: tc.muted }}>
                            {new Date(u.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        {tab === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(u)}
                              className="flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg font-medium"
                              style={{
                                backgroundColor: '#22c55e20',
                                color: '#22c55e',
                                border: '1px solid #22c55e40',
                              }}
                            >
                              <SafeIcon icon={FiCheck} className="w-3 h-3" />
                              Approve
                            </button>

                            <button
                              onClick={() => handleReject(u)}
                              className="flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg font-medium"
                              style={{
                                backgroundColor: '#ef444420',
                                color: '#ef4444',
                                border: '1px solid #ef444440',
                              }}
                            >
                              <SafeIcon icon={FiX} className="w-3 h-3" />
                              Reject
                            </button>
                          </>
                        )}

                        {tab === 'approved' && (
                          <button
                            onClick={() => handleReject(u)}
                            className="flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg font-medium"
                            style={{
                              backgroundColor: '#ef444420',
                              color: '#ef4444',
                              border: '1px solid #ef444440',
                            }}
                          >
                            <SafeIcon icon={FiX} className="w-3 h-3" />
                            Revoke
                          </button>
                        )}

                        {tab === 'rejected' && (
                          <button
                            onClick={() => handleApprove(u)}
                            className="flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg font-medium"
                            style={{
                              backgroundColor: '#22c55e20',
                              color: '#22c55e',
                              border: '1px solid #22c55e40',
                            }}
                          >
                            <SafeIcon icon={FiCheck} className="w-3 h-3" />
                            Re-approve
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}